
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle2, Mail, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function VerifyEmail() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [state, setState] = useState(token ? "verifying" : "idle");
  const [error, setError] = useState("");
  // true = sent, false = send failed, null = unknown (e.g. returning later)
  const [emailSent, setEmailSent] = useState(() => {
    try {
      const raw = sessionStorage.getItem("hm_pending_verification");
      if (raw) return JSON.parse(raw)?.email_sent === true;
    } catch (err) {
      console.warn("[verify] pending-verification parse failed", err);
    }
    return null;
  });
  const attemptedRef = React.useRef(false);

  useEffect(() => {
    if (!token) return;
    if (attemptedRef.current) return; // React StrictMode guard
    attemptedRef.current = true;
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        setState("verified");
        try { sessionStorage.removeItem("hm_pending_verification"); } catch (e) { console.warn(e); }
        try { await checkAuth(); } catch (e) { console.warn("[verify] checkAuth failed", e); }
      } catch (err) {
        // If the token was just consumed (e.g. StrictMode) but the user is now verified, treat as success.
        try {
          const me = await api.get("/auth/me");
          if (me.data?.email_verified) {
            setState("verified");
            try { sessionStorage.removeItem("hm_pending_verification"); } catch (e) { console.warn(e); }
            try { await checkAuth(); } catch (e) { console.warn("[verify] checkAuth fallback failed", e); }
            return;
          }
        } catch (e) { console.warn("[verify] /auth/me fallback failed", e); }
        setError(err.response?.data?.detail || "Something went wrong.");
        setState("error");
      }
    })();
  }, [token, checkAuth]);

  const resend = async () => {
    setState("sending");
    try {
      const { data } = await api.post("/auth/resend-verification");
      if (data.already_verified) {
        toast.success("Your email is already verified.");
        await checkAuth();
        navigate("/discover");
        return;
      }
      setEmailSent(!!data.email_sent);
      try { sessionStorage.setItem("hm_pending_verification", JSON.stringify({ email_sent: !!data.email_sent })); } catch (e) { console.warn(e); }
      if (data.email_sent) toast.success("Verification link sent. Check your inbox.");
      else toast.error(data.email_error || "We couldn't send the email. Please try again shortly.");
      setState("idle");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't resend.");
      setState("idle");
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFDF9]">
      <Navbar />
      <main className="max-w-md mx-auto px-4 py-16">
        <Card className="p-8 rounded-3xl border-[#E8DEC8] bg-white text-center" data-testid="verify-email-card">
          {state === "verifying" && (
            <>
              <div className="w-12 h-12 mx-auto border-4 border-[#D95D39] border-t-transparent rounded-full animate-spin mb-4" />
              <h1 className="font-serif text-2xl font-bold text-[#2C2623]">Verifying your email…</h1>
            </>
          )}
          {state === "verified" && (
            <>
              <div className="w-14 h-14 mx-auto rounded-full bg-[#EAF3F0] flex items-center justify-center mb-3">
                <CheckCircle2 className="w-8 h-8 text-[#2A524A]" />
              </div>
              <h1 className="font-serif text-3xl font-bold text-[#2C2623] mb-2">Email verified 🎉</h1>
              <p className="text-[#6E655F] mb-6">Thanks — your account is fully set up.</p>
              <Button
                onClick={() => navigate(user ? "/discover" : "/")}
                data-testid="verify-continue-btn"
                className="rounded-full bg-[#D95D39] hover:bg-[#C24C29] h-11 px-6">
                {user ? "Continue" : "Sign in"}
              </Button>
            </>
          )}
          {state === "error" && (
            <>
              <div className="w-14 h-14 mx-auto rounded-full bg-[#FCE8E6] flex items-center justify-center mb-3">
                <XCircle className="w-8 h-8 text-[#C5221F]" />
              </div>
              <h1 className="font-serif text-2xl font-bold text-[#2C2623] mb-2">Couldn't verify</h1>
              <p className="text-sm text-[#6E655F] mb-6">{error}</p>
              {user && (
                <Button onClick={resend} data-testid="verify-resend-btn"
                  className="rounded-full bg-[#D95D39] hover:bg-[#C24C29]">Send a fresh link</Button>
              )}
            </>
          )}
          {state === "idle" && (
            <>
              <div className="w-14 h-14 mx-auto rounded-full bg-[#FFF0EC] flex items-center justify-center mb-3">
                <Mail className="w-7 h-7 text-[#D95D39]" />
              </div>
              <h1 className="font-serif text-2xl font-bold text-[#2C2623] mb-2">
                {emailSent === false ? "We couldn't send your email" : "Check your inbox"}
              </h1>
              <p className="text-[#6E655F] mb-6" data-testid="verify-email-status">
                {emailSent === true && <>We sent a verification link to {user?.email || "your email"}. It expires in 48 hours.</>}
                {emailSent === false && <>Something went wrong sending the link to {user?.email || "your email"}. Please try again in a few minutes.</>}
                {emailSent === null && <>Verify {user?.email || "your email"} using the link we emailed you. Can't find it? Request a new one below.</>}
              </p>
              <Button onClick={resend} data-testid="verify-resend-btn"
                className="rounded-full bg-[#D95D39] hover:bg-[#C24C29] h-11 px-6 w-full">
                Resend verification link
              </Button>
            </>
          )}
        </Card>
      </main>
    </div>
  );
}
